<template>
  <div>
    <wa-dropdown v-if="!showEmployeeListDialog" placement="bottom-start" distance="2">
      <wa-button
        variant="neutral"
        appearance="outlined"
        slot="trigger"
        class="employeeSwitch"
        with-caret
      >
        {{
          deviceStore.settings.currentEmployee.name?.length > 24
            ? deviceStore.settings.currentEmployee.name.slice(0, 24) + '..'
            : deviceStore.settings.currentEmployee.name
        }}
      </wa-button>
      <div class="menu-label">Current Employee</div>
      <wa-dropdown-item>
        <div class="listItem">
          <b>{{ deviceStore.settings.currentEmployee.name }}</b
          ><br />
          <div class="text-color-neutral">{{ deviceStore.settings.currentEmployee.email }}</div>
        </div>
      </wa-dropdown-item>
      <template v-if="deviceStore.settings.deviceType !== 'Personal'">
        <wa-divider></wa-divider>
        <div class="menu-label">Store Employees</div>
        <wa-dropdown-item
          v-for="employee in settings.employees"
          :key="employee.email"
          :disabled="employee.email === deviceStore.settings.currentEmployee.email"
        >
          <div class="listItemLayout">
            <div class="listItem" @click="changeCurrentEmployee(employee)">
              {{ employee.name }}<br />
              <div class="text-color-neutral">{{ employee.email }}</div>
            </div>
            <wa-button
              v-if="
                employee.email !== deviceStore.settings.currentEmployee.email &&
                employee.email !== deviceStore.user.email
              "
              @click="removeEmployeFromStore(employee)"
              appearance="plain"
              ><wa-icon library="bootstrap" name="x-lg" label="Remove"></wa-icon
            ></wa-button>
          </div>
        </wa-dropdown-item>
        <wa-divider v-if="settings.employees.length > 0"></wa-divider>
        <wa-dropdown-item
          @click=";((showEmployeeLoginDialog = true), (showEmployeeSwitchDialog = false))"
        >
          <wa-icon library="bootstrap" slot="details" name="box-arrow-in-left"></wa-icon>
          Sign-in into {{ deviceStore.settings.warehouse }}
        </wa-dropdown-item>
      </template>
    </wa-dropdown>

    <wa-dialog
      :open="showEmployeeListDialog && !uiStore.showDeviceMarketSettingsDialog"
      without-header
      no-footer
      @wa-request-close="handleEmployeeListClose"
    >
      <h2 style="padding: 20px 0">{{ $t('employeeLogin.selectEmployee') }}</h2>
      <div v-for="employee in settings.employees" :key="employee.email">
        <div class="listItem listItem--spaced" @click="changeCurrentEmployee(employee)">
          {{ employee.name }}<br />
          <div class="text-color-neutral">{{ employee.email }}</div>
        </div>
      </div>
      <div class="listItem listItem--spaced text-right" style="padding-top: var(--wa-space-m)">
        <wa-button
          variant="neutral"
          appearance="outlined"
          @click=";((showEmployeeLoginDialog = true), (showEmployeeSwitchDialog = false))"
        >
          <wa-icon library="bootstrap" slot="end" name="box-arrow-in-left"></wa-icon>
          Sign-in into {{ deviceStore.settings.warehouse }}
        </wa-button>
      </div>
    </wa-dialog>

    <wa-dialog
      light-dismiss
      ref="employeeLoginDialog"
      no-footer
      :label="
        showEmployeeLoginDialog
          ? $t('employeeLogin.dialogLoginTitle')
          : $t('employeeLogin.dialogSwitchEmployeeTitle')
      "
      style="--width: 400px"
    >
      <EmployeeLoginDialog
        v-if="showEmployeeLoginDialog || showEmployeeSwitchDialog"
        :mode="showEmployeeSwitchDialog ? 'switch' : 'login'"
        @closeLoginDialog="closeDialog()"
      />
    </wa-dialog>
  </div>
</template>

<script setup>
import { ref, inject, defineAsyncComponent, watch, onMounted } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useUiStore } from '@/stores/ui'
import { storeToRefs } from 'pinia'

const supabase = inject('supabase')
const EmployeeLoginDialog = defineAsyncComponent(
  () => import('@/components/Employee/EmployeeLoginDialog.vue')
)

import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js'
import '@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js'
import '@awesome.me/webawesome/dist/components/divider/divider.js'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import { useNotification } from '@/composables/useNotification.js'
import { hideElement } from '@/utils/webawesome.js'

defineEmits(['closeLoginDialog'])

const deviceStore = useDeviceStore()
const uiStore = useUiStore()
const notification = useNotification()
const { settings } = storeToRefs(deviceStore)
const employeeLoginDialog = ref()
const showEmployeeLoginDialog = ref(false)
const showEmployeeSwitchDialog = ref(false)
const showEmployeeListDialog = ref(deviceStore.settings.deviceType !== 'Personal' ? true : false)

watch([showEmployeeLoginDialog, showEmployeeSwitchDialog], ([newLD, newSD]) => {
  if (newLD || newSD) {
    employeeLoginDialog.value.open = true
  }
})

onMounted(() => {
  setTimeout(() => {
    employeeLoginDialog.value?.addEventListener('wa-hide', (event) => {
      if (event.target !== employeeLoginDialog.value) return

      event.stopPropagation()
      showEmployeeLoginDialog.value = false
      showEmployeeSwitchDialog.value = false
    })
  }, 200)

  document.addEventListener('visibilitychange', function () {
    if (
      document.visibilityState === 'visible' &&
      !showEmployeeListDialog.value &&
      deviceStore.settings.deviceType !== 'Personal'
    ) {
      showEmployeeListDialog.value = true
    }
  })
})

function closeDialog() {
  showEmployeeListDialog.value = false
  showEmployeeLoginDialog.value = false
  showEmployeeSwitchDialog.value = false
  hideElement(employeeLoginDialog.value)
}

function handleEmployeeListClose(event) {
  event.stopPropagation()
  if (['overlay', 'keyboard'].includes(event.detail.source)) {
    event.preventDefault()
  }
}

function changeCurrentEmployee() {
  showEmployeeSwitchDialog.value = true
}

async function removeEmployeFromStore(employee) {
  const { error } = await supabase
    .from('store_employees')
    .delete()
    .eq('employee_id', employee.id)
    .eq('store_warehouse', deviceStore.settings.warehouse)
    .eq('app_environment', import.meta.env.VITE_WEB_ENV)

  if (!error) {
    deviceStore.removeStoreEmployee(employee)
    notification.showNotification({
      message: `${employee.name ? employee.name : 'Employee'} has been removed from the store.`,
      icon: 'person-dash'
    })
  }
}
</script>

<style lang="postcss" scoped>
wa-dropdown-item {
  font-size: var(--wa-font-size-s);
}

.listItemLayout {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.listItem {
  line-height: var(--wa-line-height-condensed);
  min-width: 180px;
  font-size: var(--wa-font-size-s);
  cursor: pointer;

  &--spaced {
    border-top: 1px solid var(--wa-color-neutral-70);
    padding: var(--wa-space-xs) 0;
  }
}
</style>
